import { Box, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import CaptionedLinearLoader from 'pages/calculator/MediaPlanning/components/Touchpoints/CaptionedLinearLoader/CaptionedLinearLoader';
import SlidersError from 'pages/calculator/MediaPlanning/components/Touchpoints/SlidersError/SlidersError';
import TargetGroupSelectors from 'pages/calculator/MediaPlanning/components/Touchpoints/MetaTouchpoint/TargetGroupSelectors/TargetGroupSelectors';
import PartnerSelectors from 'pages/calculator/MediaPlanning/components/Touchpoints/MetaTouchpoint/PartnersSelectors/PartnersSelectors';
import usePlanStore from 'store/planStore';
import { useQuery } from 'react-query';
import { DIGITAL_FAIR_VALUE_KEY } from 'api/services/digitalFairValue';
import { useEffect } from 'react';
import useCampaignLength from '../../../Plan/Countries/hooks/useCampaignLength';
import useMetaConfig from '../hooks/useMetaConfig';
import useFanpages from './hooks/useFanpages';
import { getPartners } from '../../../../../../api/services/mediaPlanning/getPartners';

function MetaTouchpoint() {
  const { t } = useTranslation();
  const {
    selectedCountry,
    generalInfoState,
    selectedCountryISOCode,
    countryForms,
    updateMetaConfig,
  } = usePlanStore();

  const metaConfig = countryForms[selectedCountry!]?.metaConfig;

  const {
    fetchMetaConfig,
    data: fieldConfig,
    isFetching: isFieldConfigFetching,
  } = useMetaConfig();

  const { data: campaignLengths, refetch: refetchCampaignLenghts } =
    useCampaignLength(selectedCountry!);

  const isMetaRF = countryForms[selectedCountry!].availableTouchpoints?.find(
    (touchpoint) =>
      touchpoint.touchpointFunnel.toLowerCase() ===
      'Reservation Awareness (RF)'.toLowerCase()
  );

  const { data: fanpages } = useFanpages(selectedCountry!, !!isMetaRF);

  useEffect(() => {
    if (selectedCountryISOCode) {
      refetchCampaignLenghts();
    }
  }, [selectedCountryISOCode]);

  useEffect(() => {
    if (!countryForms[selectedCountry!].metaConfig?.targetGroup) {
      fetchMetaConfig(undefined);
    }
  }, [countryForms[selectedCountry!].metaConfig?.targetGroup]);

  const {
    data: partnersData,
    isLoading: isLoadingPartners,
    isError: isErrorPartners,
  } = useQuery(
    [
      DIGITAL_FAIR_VALUE_KEY,
      'partners-meta',
      selectedCountryISOCode,
      generalInfoState.division,
      generalInfoState.brand,
    ],
    () =>
      getPartners(
        selectedCountryISOCode,
        generalInfoState.division,
        generalInfoState.brand
      )
  );

  const handleChangeStateValues = (key: string, value: any) => {
    updateMetaConfig(selectedCountry!, { [key]: value });
  };

  if (isErrorPartners) {
    return <SlidersError error={`${t('mediaPlanning.loadingError')}`} />;
  }

  if (isLoadingPartners || isFieldConfigFetching) {
    return (
      <CaptionedLinearLoader
        caption={
          <Typography textAlign="center">
            <Trans i18nKey="loaders.holdOn" />
          </Typography>
        }
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        pt: '13px',
        pb: '20px',
        rowGap: '38px',
      }}
    >
      <TargetGroupSelectors
        targetGroupSaturation={metaConfig?.saturation || 0}
        startDate={metaConfig?.startDate}
        fetchedCampaignLengthsData={campaignLengths}
        fetchedFanpagesData={fanpages}
        handleChangeStateValues={handleChangeStateValues}
        minMaxSelectorsValues={metaConfig}
        fetchMetaConfig={fetchMetaConfig}
        isMetaRF={!!isMetaRF}
      />
      <PartnerSelectors
        partners={metaConfig?.partners || []}
        partnerBudgetShareConsideration={
          metaConfig?.partnerBudgetShareConsideration || 0
        }
        fetchedPartnersData={{
          partners: partnersData?.data?.partners || [],
          collabPartners: partnersData?.data?.collabPartners || [],
        }}
        handleChangeStateValues={handleChangeStateValues}
      />
    </Box>
  );
}

export default MetaTouchpoint;
